import React from 'react';
import { X } from 'lucide-react';
import { DEFAULT_FINANCIAL_SETTINGS } from '../utils/careerGrowth';

/**
 * Advanced Settings Modal Component (App-level)
 * Allows users to customize financial assumptions and career growth model
 * Settings apply to the currently active vision
 */
export const AdvancedSettingsModal = ({ vision, onUpdate, onClose }) => {
  // Get current settings or use defaults
  const currentSettings = vision?.financialSettings || DEFAULT_FINANCIAL_SETTINGS;

  const handleSettingChange = (key, value) => {
    console.log(`⚙️ Setting changed: ${key} = ${value}`);
    const updatedSettings = {
      ...currentSettings,
      [key]: value
    };
    console.log('⚙️ Updated settings:', updatedSettings);

    onUpdate({
      financialSettings: updatedSettings
    });
  };

  const resetToDefaults = () => {
    if (window.confirm('Reset all advanced settings to defaults?')) {
      onUpdate({
        financialSettings: DEFAULT_FINANCIAL_SETTINGS
      });
    }
  };

  if (!vision) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md">
          <p className="text-gray-700">Please select or create a vision first.</p>
          <button
            onClick={onClose}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">Advanced Financial Settings</h2>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-8">
              {/* Career Growth Section */}
              <section>
                <h3 className="text-lg font-semibold mb-4 text-blue-600">Career Growth Model</h3>

                <div className="space-y-4">
                  {/* Smart vs Simple Mode */}
                  <div className="border rounded p-4 bg-gray-50">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={currentSettings.useSmartCareerMode}
                        onChange={(e) => handleSettingChange('useSmartCareerMode', e.target.checked)}
                        className="w-5 h-5"
                      />
                      <div>
                        <p className="font-medium">Use Smart Career Mode</p>
                        <p className="text-sm text-gray-600">
                          Realistic age-based growth: faster early career (6%), slower later (2%)
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Simple Mode Rate */}
                  {!currentSettings.useSmartCareerMode && (
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Annual Income Growth Rate (Simple Mode)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={(currentSettings.simpleGrowthRate * 100).toFixed(1)}
                        onChange={(e) => handleSettingChange('simpleGrowthRate', parseFloat(e.target.value) / 100)}
                        className="border rounded px-3 py-2 w-32"
                      />
                      <span className="ml-2 text-gray-600">%</span>
                    </div>
                  )}

                  {/* Smart Mode Adjustments */}
                  {currentSettings.useSmartCareerMode && (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Early Career Boost (22-30 years)
                        </label>
                        <input
                          type="range"
                          min="0.5"
                          max="2.0"
                          step="0.1"
                          value={currentSettings.earlyCareerBoost}
                          onChange={(e) => handleSettingChange('earlyCareerBoost', parseFloat(e.target.value))}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-gray-600">
                          <span>50% slower</span>
                          <span className="font-medium">{(currentSettings.earlyCareerBoost * 100).toFixed(0)}%</span>
                          <span>200% faster</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Base: 6%/year → Adjusted: {(0.06 * currentSettings.earlyCareerBoost * 100).toFixed(1)}%/year
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Late Career Adjustment (46-60 years)
                        </label>
                        <input
                          type="range"
                          min="0.5"
                          max="1.5"
                          step="0.1"
                          value={currentSettings.lateCareerPenalty}
                          onChange={(e) => handleSettingChange('lateCareerPenalty', parseFloat(e.target.value))}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-gray-600">
                          <span>50% slower</span>
                          <span className="font-medium">{(currentSettings.lateCareerPenalty * 100).toFixed(0)}%</span>
                          <span>150% faster</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Base: 2%/year → Adjusted: {(0.02 * currentSettings.lateCareerPenalty * 100).toFixed(1)}%/year
                        </p>
                      </div>
                    </>
                  )}

                  {/* Auto Promotions */}
                  <div className="border rounded p-4 bg-gray-50">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={currentSettings.enableAutoPromotions}
                        onChange={(e) => handleSettingChange('enableAutoPromotions', e.target.checked)}
                        className="w-5 h-5"
                      />
                      <div>
                        <p className="font-medium">Enable Automatic Promotions</p>
                        <p className="text-sm text-gray-600">
                          Milestone promotions at ages 25, 30, 35, 40, 45 (if 2+ years since last)
                        </p>
                      </div>
                    </label>
                  </div>

                  {currentSettings.enableAutoPromotions && (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Promotion Frequency
                        </label>
                        <input
                          type="range"
                          min="0.5"
                          max="2.0"
                          step="0.1"
                          value={currentSettings.promotionFrequency}
                          onChange={(e) => handleSettingChange('promotionFrequency', parseFloat(e.target.value))}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-gray-600">
                          <span>Less frequent</span>
                          <span className="font-medium">{(currentSettings.promotionFrequency * 100).toFixed(0)}%</span>
                          <span>More frequent</span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Promotion Size Multiplier
                        </label>
                        <input
                          type="range"
                          min="0.5"
                          max="2.0"
                          step="0.1"
                          value={currentSettings.promotionMultiplier}
                          onChange={(e) => handleSettingChange('promotionMultiplier', parseFloat(e.target.value))}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-gray-600">
                          <span>Smaller raises</span>
                          <span className="font-medium">{(currentSettings.promotionMultiplier * 100).toFixed(0)}%</span>
                          <span>Larger raises</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Early career: {(15 * currentSettings.promotionMultiplier).toFixed(0)}% raise |
                          Mid career: {(12 * currentSettings.promotionMultiplier).toFixed(0)}% raise
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </section>

              {/* Economic Assumptions */}
              <section>
                <h3 className="text-lg font-semibold mb-4 text-green-600">Economic Assumptions</h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Annual Inflation Rate
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={(currentSettings.inflationRate * 100).toFixed(1)}
                      onChange={(e) => handleSettingChange('inflationRate', parseFloat(e.target.value) / 100)}
                      className="border rounded px-3 py-2 w-32"
                    />
                    <span className="ml-2 text-gray-600">%</span>
                    <p className="text-xs text-gray-500 mt-1">
                      Historical average: 2-3%. Used for expense and income adjustments.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Investment Return Rate (Nominal)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={(currentSettings.investmentReturn * 100).toFixed(1)}
                      onChange={(e) => handleSettingChange('investmentReturn', parseFloat(e.target.value) / 100)}
                      className="border rounded px-3 py-2 w-32"
                    />
                    <span className="ml-2 text-gray-600">%</span>
                    <p className="text-xs text-gray-500 mt-1">
                      Historical S&P 500: ~10%. Your portfolio growth rate before inflation.
                    </p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded p-3">
                    <p className="text-sm font-medium text-blue-800">
                      Real Return: {((currentSettings.investmentReturn - currentSettings.inflationRate) * 100).toFixed(1)}%
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      This is your actual purchasing power growth after accounting for inflation.
                    </p>
                  </div>
                </div>
              </section>

              {/* Retirement Assumptions */}
              <section>
                <h3 className="text-lg font-semibold mb-4 text-purple-600">Retirement Assumptions</h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Social Security Benefit Multiplier
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1.5"
                      step="0.05"
                      value={currentSettings.socialSecurityMultiplier}
                      onChange={(e) => handleSettingChange('socialSecurityMultiplier', parseFloat(e.target.value))}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-gray-600">
                      <span>0% (none)</span>
                      <span className="font-medium">{(currentSettings.socialSecurityMultiplier * 100).toFixed(0)}%</span>
                      <span>150% (optimistic)</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Set to 0% if you don't expect Social Security. 100% = full estimated benefit.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Withdrawal Rate (4% Rule)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      defaultValue={(currentSettings.withdrawalRate * 100).toFixed(1)}
                      key={`withdrawal-${currentSettings.withdrawalRate}`}
                      onBlur={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value) && value > 0 && value <= 10) {
                          console.log(`🔄 Updating withdrawal rate to ${value}%`);
                          handleSettingChange('withdrawalRate', value / 100);
                        }
                      }}
                      className="border rounded px-3 py-2 w-32"
                    />
                    <span className="ml-2 text-gray-600">%</span>
                    <p className="text-xs text-gray-500 mt-1">
                      How much of your portfolio you can safely withdraw annually in retirement.
                    </p>
                  </div>
                </div>
              </section>

              {/* 401k Settings */}
              <section>
                <h3 className="text-lg font-semibold mb-4 text-orange-600">401(k) Settings</h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Employer Match Rate
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      value={(currentSettings.retirement401kMatchRate * 100).toFixed(1)}
                      onChange={(e) => handleSettingChange('retirement401kMatchRate', parseFloat(e.target.value) / 100)}
                      className="border rounded px-3 py-2 w-32"
                    />
                    <span className="ml-2 text-gray-600">%</span>
                    <p className="text-xs text-gray-500 mt-1">
                      Typical: 50-100% match up to a certain percentage of salary.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Maximum Match Percentage
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      value={(currentSettings.retirement401kMaxMatch * 100).toFixed(1)}
                      onChange={(e) => handleSettingChange('retirement401kMaxMatch', parseFloat(e.target.value) / 100)}
                      className="border rounded px-3 py-2 w-32"
                    />
                    <span className="ml-2 text-gray-600">%</span>
                    <p className="text-xs text-gray-500 mt-1">
                      Employer matches up to this % of your salary (e.g., 6% is common).
                    </p>
                  </div>
                </div>
              </section>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-between">
              <button
                onClick={resetToDefaults}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
              >
                Reset to Defaults
              </button>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
              >
                Done
              </button>
            </div>
          </div>
        </div>
  );
};

export default AdvancedSettingsModal;
